import * as yup from "yup";
import Link from "next/link";
import Image from "next/image";
import Toast from "@components/toast";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import { useForm } from "react-hook-form";
import { useSession } from "next-auth/react";
import { useSettings } from "@hooks/settings";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import {
  capturePlatformAttribution,
  getPlatformAttribution,
} from "@lib/platformAttribution";

const schema = yup.object().shape({
  email: yup.string().email().required(),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  password: yup.string().min(8).required(),
  otp: yup.string().required("OTP is required"),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required(),
});

const emailSchema = yup.object().shape({
  email: yup.string().email().required("Email is required"),
  otp: yup.string().required("OTP is required"),
});

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["auth"])),
    },
  };
}

const CompanyBuildingScreen = ({ settings, t, delayed, retrying, onRetry }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setProgress(100), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="container min-vh-100 d-flex flex-column justify-content-center align-items-center">
      <div className="text-center">
        <div className="mb-4">
          <Image
            src={settings.app_logo}
            alt={settings.app_name}
            width={120}
            height={120}
            priority
            className="d-block mx-auto"
            style={{ width: "auto", height: "auto" }}
          />
        </div>

        <div
          className="spinner-border text-primary mb-4"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden">{t("auth:setup.loading")}</span>
        </div>

        <h2 className="display-6 mb-3">{t("auth:setup.title")}</h2>

        <p className="text-muted lead mb-5">{t("auth:setup.subtitle")}</p>

        {delayed && (
          <div className="alert alert-warning" role="status">
            <p className="mb-3">A configuração está demorando mais que o esperado. Tente novamente para concluir sua área de trabalho.</p>
            <button type="button" className="btn btn-outline-primary" onClick={onRetry} disabled={retrying}>
              {retrying ? "Tentando novamente..." : "Tentar novamente"}
            </button>
          </div>
        )}

        <div
          className="progress"
          style={{ height: "6px", width: "300px", margin: "0 auto" }}
        >
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{
              width: `${progress}%`,
              transition: "width 1s linear",
              backgroundColor: "var(--bs-bota-light-blue, #0d6efd)",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [companySetup, setCompanySetup] = useState(false);
  const [conditions, setConditions] = useState({});
  const [referral, setReferral] = useState(null);
  const [otpEmail, setOtpEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [next, setNext] = useState(false);
  const [provisioningDelayed, setProvisioningDelayed] = useState(false);
  const [provisioningContext, setProvisioningContext] = useState(null);
  const [provisioningAttempt, setProvisioningAttempt] = useState(0);
  const [retryingProvisioning, setRetryingProvisioning] = useState(false);
  const { data: session, status, update } = useSession();
  const [claimProfile, setClaimProfile] = useState(null);
  const [claimRedirect, setClaimRedirect] = useState("/dashboard");
  const { t } = useTranslation("auth");
  const router = useRouter();
  const notify = React.useCallback((type, message) => {
    Toast({ type, message });
  }, []);
  const settings = useSettings();
  const { aff_pn, aff, claim, plan_id: directPlanId } = router.query;

  useEffect(() => {
    capturePlatformAttribution();
  }, []);



  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: errorsEmail },
    trigger,
    getValues: getValuesEmail,
    reset: resetEmail,
  } = useForm({
    resolver: yupResolver(emailSchema),
  });

  useEffect(() => {
    if (!router.isReady || typeof claim !== "string") return;
    fetch(
      "/api/v1/lead-forms/public/submissions/" +
        encodeURIComponent(claim) +
        "?claim=1",
    )
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Teste de agente invalido");
        const profile = data.claim_profile;
        setClaimProfile(profile);
        resetEmail({ email: profile.email, otp: "" });
        reset({
          email: profile.email,
          first_name: profile.first_name || "",
          last_name: profile.last_name || "",
        });
        setClaimRedirect(
          data.selected_plan?.redirect_after_signup ||
            data.form?.redirect_after_signup ||
            "/dashboard",
        );
      })
      .catch((claimError) => notify("error", claimError.message));
  }, [
    router.isReady,
    claim,
    notify,
    reset,
    resetEmail,
  ]);

  useEffect(() => {
    if (!companySetup) return undefined;

    let cancelled = false;
    const redirectTo =
      typeof router.query.callbackUrl === "string"
        ? router.query.callbackUrl
        : claimRedirect;

    const pollProvisioning = async () => {
      for (let attempt = 0; attempt < 45 && !cancelled; attempt += 1) {
        try {
          const statusUrl =
            typeof claim === "string"
              ? "/api/auth/signup/status?claim=" + encodeURIComponent(claim)
              : "/api/auth/signup/status";
          const response = await fetch(statusUrl);
          const data = response.ok ? await response.json() : null;
          if (data?.ready) {
            await update?.();
            if (!cancelled) await router.replace(redirectTo);
            return;
          }
        } catch (error) {
          console.warn("[signup] provisioning status unavailable", error);
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      if (!cancelled) setProvisioningDelayed(true);
    };

    void pollProvisioning();
    return () => {
      cancelled = true;
    };
  }, [companySetup, provisioningAttempt, router, update, claimRedirect, claim]);
  const onSubmit = async (data) => {
    setLoading(true);

    const affiliate = typeof aff === "string" ? aff : null;
    const affiliatePlan =
      typeof claim === "string" && typeof directPlanId === "string"
        ? directPlanId
        : typeof aff_pn === "string"
          ? aff_pn
          : null;

    fetch("/api/auth/signup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...data,
        aff: affiliate || null,
        plan_id: affiliatePlan || null,
        claim: typeof claim === "string" ? claim : null,
        attribution: getPlatformAttribution(),
      }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.json();
        throw new Error(text.error || text.message || "Something went wrong. Please try again.");
      })
      .then(async (response) => {
        notify("success", response.message);

        const loginResult = await signIn("credentials", {
          redirect: false,
          email: data.email,
          password: data.password,
          callbackUrl:
            typeof router.query.callbackUrl === "string"
              ? router.query.callbackUrl
              : window.location.origin,
        });

        if (loginResult?.error) {
          throw new Error("Your account was created, but automatic sign-in failed. Please sign in.");
        }

        setProvisioningContext({
          aff: affiliate,
          plan_id: affiliatePlan,
          claim: typeof claim === "string" ? claim : null,
          attribution: getPlatformAttribution(),
        });
        setProvisioningDelayed(false);
        setCompanySetup(true);
      })
      .catch((error) => {
        notify("error", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const sendOTP = async (email) => {
    if (!email) return;
    setLoading(true);
    fetch("/api/auth/signup/send-otp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.json();
        throw new Error(text.error || text.message || "Something went wrong. Please try again.");
      })
      .then((data) => {
        setOtpEmail(email);
        notify("success", data.message);
      })
      .catch((err) => {
        notify("error", err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const verifyOTP = async (data) => {
    setLoading(true);
    fetch("/api/auth/signup/verify-otp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: data.email, otp: data.otp }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.json();
        throw new Error(text.error || text.message || "Something went wrong. Please try again.");
      })
      .then((res) => {
        reset({
          email: data.email,
          otp: data.otp,
          first_name: claimProfile?.first_name || "",
          last_name: claimProfile?.last_name || "",
        });
        setNext(true);
        notify("success", res.message);
      })
      .catch((err) => {
        notify("error", err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!companySetup && status != "loading" && session) {
    notify("info", t("auth:alreadySignedIn"));

    router.query.callbackUrl
      ? router.push(router.query.callbackUrl)
      : router.push(
          `${
            router.locale === router.defaultLocale ? "" : `/${router.locale}`
          }/${session?.user?.language}/dashboard`,
        );

    return null;
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const checkPasswordStrength = (value) => {
    const hasMinimumLength = value.length >= 8;
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);

    return {
      strength:
        (hasMinimumLength +
          hasSpecialCharacter +
          hasNumber +
          hasUppercase +
          hasLowercase) /
        5,
      conditions: {
        minimumLength: hasMinimumLength,
        specialCharacter: hasSpecialCharacter,
        number: hasNumber,
        uppercase: hasUppercase,
        lowercase: hasLowercase,
      },
    };
  };

  const handlePasswordChange = (event) => {
    const { strength, conditions } = checkPasswordStrength(event.target.value);
    setConditions(conditions);
  };

  if (companySetup) {
    return <CompanyBuildingScreen settings={settings} t={t} delayed={provisioningDelayed} retrying={retryingProvisioning} onRetry={async () => {
      setRetryingProvisioning(true);
      try {
        const response = await fetch("/api/auth/signup/status", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(provisioningContext || {}) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Unable to retry workspace setup.");
        setProvisioningDelayed(false);
        setProvisioningAttempt((attempt) => attempt + 1);
      } catch (error) { notify("error", error.message); } finally { setRetryingProvisioning(false); }
    }} />;
  }

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="d-none d-md-block col-md-8 col-lg-6 col-xl-3">
          <Image
            src="/images/sign-up-animate.svg"
            alt="Sign Up"
            width={300}
            height={300}
            priority
            className="d-block mx-auto mb-4"
            style={{ width: "auto", height: "auto" }}
          />
        </div>

        <div className="col-12 col-md-8 col-lg-6 col-xl-4">
          <div className="card">
            <div className="card-header">
              <Image
                src={settings.app_logo}
                alt={settings.app_name}
                width={100}
                height={100}
                priority
                className="d-block mx-auto mb-4"
                style={{ width: "auto", height: "auto" }}
              />
              <h1 className="display-6 text-center mb-3">
                {t("auth:signUp.title")}
              </h1>
            </div>

            <div className="card-body">
              {next ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <input hidden {...register("otp")} />
                  <div className="mb-3">
                    <label className="form-label">
                      {t("auth:fields.email.label")}
                    </label>
                    <input
                      className="form-control form-control-lg"
                      type="email"
                      {...register("email")}
                      disabled
                    />
                    {errors.email && (
                      <div className="text-danger">{errors.email.message}</div>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label">
                        {t("auth:fields.first-name")}
                        <i className="fas fa-asterisk text-danger ms-1" />
                      </label>
                      <input
                        className="form-control form-control-lg"
                        type="text"
                        {...register("first_name")}
                      />

                      <div className="text-danger">
                        {errors?.first_name?.message}
                      </div>
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label">
                        {t("auth:fields.last-name")}
                        <i className="fas fa-asterisk text-danger ms-1" />
                      </label>
                      <input
                        className="form-control form-control-lg"
                        type="text"
                        {...register("last_name")}
                      />

                      <div className="text-danger">
                        {errors?.last_name?.message}
                      </div>
                    </div>
                  </div>

                  {/* password */}
                  <div className="mb-3">
                    <label className="form-label">
                      {t("auth:fields.password.label")}
                      <i className="fas fa-asterisk text-danger ms-1" />
                    </label>
                    <div className="input-group flex-nowrap">
                      <input
                        className="form-control"
                        type={showPassword ? "text" : "password"}
                        {...register("password")}
                        onChange={handlePasswordChange}
                      />
                      <span className="input-group-text" id="addon-wrapping">
                        <i
                          className={`far 
                            ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                          onClick={togglePasswordVisibility}
                        />
                      </span>
                    </div>

                    <ul className="list-unstyled row mt-2">
                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.uppercase
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-uppercase")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.lowercase
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-lowercase")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.specialCharacter
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-special-character")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.number
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-number")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.minimumLength
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:8-characters")}
                      </li>
                    </ul>

                    {errors.password && (
                      <div className="text-danger">
                        {errors.password.message}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      {t("auth:fields.passwordConfirm.label")}
                      <i className="fas fa-asterisk text-danger ms-1" />
                    </label>
                    <input
                      className="form-control form-control-lg"
                      type="password"
                      {...register("passwordConfirm")}
                    />
                    {errors.passwordConfirm && (
                      <div className="text-danger">
                        {errors.passwordConfirm.message}
                      </div>
                    )}
                  </div>

                  <div className="text-center mt-3">
                    <button
                      type="submit"
                      className="btn btn-lg btn-bota-light-blue"
                      disabled={loading}
                    >
                      {t("auth:signUp.title")}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmitEmail(verifyOTP)}>
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label">
                        {t("auth:fields.email.label")}
                        <i className="fas fa-asterisk text-danger ms-1" />
                      </label>
                      <input
                        className="form-control form-control-lg"
                        type="email"
                        {...registerEmail("email", {
                          onChange: () => {
                            setOtpEmail(null);
                          },
                        })}
                      />
                      {errorsEmail.email && (
                        <div className="text-danger">
                          {errorsEmail.email.message}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-12 mb-3">
                      <div className="text-center mt-4">
                        <button
                          type="button"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                          onClick={async () => {
                            const result = await trigger("email");
                            result && sendOTP(getValuesEmail("email"));
                          }}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-grow spinner-grow-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Loading...
                            </>
                          ) : otpEmail ? (
                            t("auth:resendOTP")
                          ) : (
                            t("auth:verifyEmail")
                          )}
                        </button>
                      </div>
                    </div>
                    {otpEmail && (
                      <>
                        <div className="col-12 col-md-6 mb-3">
                          <label className="form-label">
                            {t("auth:fields.otp.label")}
                            <span className="text-danger ms-1">
                              <i className="fas fa-asterisk fa-xs" />
                            </span>
                          </label>
                          <input
                            className="form-control form-control-lg"
                            type="text"
                            {...registerEmail("otp")}
                          />
                          {errorsEmail.otp && (
                            <div className="text-danger">
                              {errorsEmail.otp.message}
                            </div>
                          )}
                        </div>
                        <div className="col-12 col-md-12 mb-3">
                          <div className="text-end mt-4">
                            <button
                              type="submit"
                              className="btn btn-lg btn-primary"
                              disabled={loading}
                            >
                              {t("auth:next")}
                              <i className="fas fa-arrow-right ms-2" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </form>
              )}

              <div className="row jsutify-content-end">
                <div className="col-12 mb-3">
                  <p className="text-end">
                    <small className="text-muted text-center">
                      {t("auth:alreadyHaveAccount")}{" "}
                      <Link
                        href={
                          router.query.callbackUrl
                            ? `/auth/signin?callbackUrl=${encodeURIComponent(
                                router.query.callbackUrl,
                              )}`
                            : "/auth/signin"
                        }
                        style={{
                          color: "var(--bs-bota-light-blue)",
                        }}
                      >
                        {t("auth:signIn.title")}
                      </Link>
                    </small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
